/**
 * StepPlaceholder — used for pipeline steps whose dedicated screen is
 * scheduled for a later sprint. Keeps the flow visible (no dead ends)
 * without pretending to ship functionality that doesn't exist yet.
 */
import { Link, useParams } from "react-router-dom";
import { Info, Sparkle, Archive } from "@phosphor-icons/react";

export default function StepPlaceholder() {
  const { pid, stepId } = useParams();
  return (
    <div className="p-6 space-y-4 max-w-3xl" data-testid={`step-placeholder-${stepId}`}>
      <header>
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-slate-500">Pipeline step</div>
        <h1 className="font-heading font-black text-2xl tracking-tight text-slate-900">
          {(stepId || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
        </h1>
      </header>

      <div className="rounded-lg border border-brand-blue/20 bg-brand-blue/5 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <Sparkle size={16} className="text-brand-blue mt-0.5" weight="fill" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[13px] text-slate-900">This step is part of the 20-step orchestration.</div>
            <p className="text-[12px] text-slate-700 mt-1">
              The dedicated screen for this step is scheduled for a later delivery sprint.
              Interim outputs and inputs remain available in the neighbouring steps and in the
              Project Data Bank so no data is lost.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/app/projects/${pid}/setup`} data-testid="ph-open-cc"
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-blue/30 hover:bg-brand-blue/10 px-2.5 py-1.5 text-[11.5px] text-brand-blue">
            Open Project Configuration
          </Link>
          <Link to={`/app/projects/${pid}/gis`} data-testid="ph-open-gis"
                  className="inline-flex items-center gap-1.5 rounded-md border border-brand-blue/30 hover:bg-brand-blue/10 px-2.5 py-1.5 text-[11.5px] text-brand-blue">
            Open GIS Studio
          </Link>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 text-[11.5px] text-slate-600 flex items-start gap-2">
        <Info size={11} className="text-slate-400 mt-0.5" />
        <div>
          Existing backend capabilities for this step are already reachable via the neighbouring wizard screens.
          When the dedicated UI ships, all previously saved inputs and outputs will be surfaced here automatically —
          no re-entry required.
        </div>
      </div>
    </div>
  );
}
